<template>
  <div>
    <v-row>
      <v-col
        v-if="isDayShown"
        v-bind="columnsAlignment.day"
      >
        <validation-provider
          v-slot="{ failed }"
          :vid="`${vid}_day`"
          name="Day"
          :rules="getDayRules(date)"
        >
          <qz-number-input
            v-model="date.day"
            :data-test="fieldTrackerId.day"
            label="Day"
            :error="failed || isParsedDateProviderFailed"
            :placeholder="shortPlaceholder ? 'Day' : 'Enter day'"
            :maxlength="inputCharsLimiters.DAY"
            :without-separators="true"
            @blur="onDayBlur"
            @paste.prevent
          />
        </validation-provider>
      </v-col>
      <v-col
        v-bind="columnsAlignment.month"
      >
        <validation-provider
          v-slot="{ failed }"
          :vid="`${vid}_month`"
          name="Month"
          rules="listOptionRequired"
        >
          <qz-select
            v-model="date.month"
            :data-test="fieldTrackerId.month"
            :items="monthOptions"
            :error="failed || isParsedDateProviderFailed"
            label="Month"
            :placeholder="shortPlaceholder ? 'Month' : 'Select month'"
          />
        </validation-provider>
      </v-col>
      <v-col
        v-bind="columnsAlignment.year"
      >
        <validation-provider
          v-slot="{ failed }"
          :vid="`${vid}_year`"
          name="Year"
          rules="listOptionRequired"
        >
          <qz-select
            v-model="date.year"
            :data-test="fieldTrackerId.year"
            :items="yearOptions"
            :error="failed || isParsedDateProviderFailed"
            label="Year"
            :placeholder="shortPlaceholder ? 'Year' : 'Select year'"
          />
        </validation-provider>
      </v-col>
    </v-row>
    <validation-provider
      v-slot="{ errors, failedRules }"
      ref="parsedDateProvider"
      :vid="vid"
      :rules="dateRule"
    >
      <input
        type="hidden"
        :value="parsedDate"
      >
      <div
        v-if="showDateError"
        class="error--text mt-2 px-3 fz12"
      >
        {{ getDateErrors(failedRules, errors) }}
      </div>
    </validation-provider>
  </div>
</template>

<script>
import {
  padStart,
} from 'lodash';

import { monthsOptions } from '@/helpers/common';

import {
  ExtDate,
  checkDateObjFilled,
  DATE_FORMAT,
} from '@/helpers/date';

import useDateSelect from '@/composables/useDateSelect.js';

export default {
  name: 'qz-date-select',
  inject: [
    'helpers',
    'inputCharsLimiters',
  ],
  model: {
    prop: 'value',
    event: 'date-change',
  },
  props: {
    formObserver: {
      type: Object,
      required: true,
    },
    vid: {
      type: String,
      required: true,
    },
    yearOptions: {
      type: Array,
      required: true,
    },
    monthOptions: {
      type: Array,
      default: () => monthsOptions,
    },
    dateRule: {
      type: [String, Object],
      required: true,
    },
    value: {
      type: String,
      default: null,
    },
    columnsAlignment: {
      type: Object,
      default: () => ({
        day: {
          cols: '12',
          sm: '4',
        },
        month: {
          cols: '12',
          sm: '4',
        },
        year: {
          cols: '12',
          sm: '4',
        },
      }),
    },
    shortPlaceholder: {
      type: Boolean,
      default: false,
    },
    isDayShown: {
      type: Boolean,
      default: true,
    },
    validationErrors: {
      type: Object,
      default: null,
    },
  },
  data () {
    return {
      date: {},
      parsedDate: null,
      parsedDateProvider: null,
      providerShadowState: null,
    };
  },
  computed: {
    showDateError () {
      if (!Object.values(this.formObserver?.errors).length) return false;

      return Object.keys(this.formObserver?.errors).some(key => {
        if (key.includes(this.vid)) {
          return this.formObserver?.errors[key].length === 1;
        }
      });
    },
    isParsedDateProviderFailed () {
      if (!this.parsedDateProvider) return false;
      return this.parsedDateProvider?.flags.failed;
    },
    isDayFieldInvalid () {
      if (!this.date?.day) return false;
      if (this.formObserver?.fields[this.vid + '_day']?.failedRules?.required) return false;
      return this.formObserver?.errors[this.vid + '_day'].length;
    },
    fieldTrackerId () {
      return {
        day: `${this.vid}_day`,
        month: `${this.vid}_month`,
        year: `${this.vid}_year`,
      };
    },
  },
  created () {
    if (this.value) {
      this.date = this.getDateObj(new ExtDate(this.value));
      this.change();
    }

    this.$watch('value', val => {
      const dateInstance = new ExtDate(val);
      if (val !== this.parsedDate) {
        this.date = this.getDateObj(dateInstance);
        this.parsedDate = dateInstance.isValid() ? dateInstance.format() : null;
      }
    });
    this.$watch('date', this.change, { deep: true });
  },
  async mounted () {
    this.setParsedDateProvider();
    await this.setProviderShadowState();
  },
  watch: {
    async parsedDate (value) {
      if (value) {
        // Added nextTick to get the proper value in
        // parsedDataProvider and perform validation
        // before render change of parsedDataProvider state on the page.
        await this.$nextTick();
        this.providerShadowState = await this.parsedDateProvider.validateSilent();
        this.parsedDateProvider.validate();
      }
    },
  },
  methods: {
    // Add 0 to day if day value is not two-digit number
    // or clear input if its 0
    onDayBlur (event) {
      const dayValue = +event.target.value === 0 ? '' : padStart(+this.date.day, 2, 0);

      this.$set(this.date, 'day', dayValue);
    },
    /**
     * Convert {@link LOCAL_DATE_TEMPLATE} type Object ( date without day) into ExtDate
     * @param {Object} dateObj - {@link LOCAL_DATE_TEMPLATE} type object
     * @returns {ExtDate}
     */
    convertDateObjWithoutDayToDate (dateObj) {
      const FIRST_DAY_OF_MONTH = 1;

      return new ExtDate(dateObj.year, dateObj.month, FIRST_DAY_OF_MONTH).parse(`${dateObj.year}-${padStart(dateObj.month, 2, '0')}-${padStart(FIRST_DAY_OF_MONTH.toString(), 2, '0')}`, DATE_FORMAT);
    },
    /**
     * Return Object with rules based on date passed in dateObj param
     * @param {Object} dateObject - {@link LOCAL_DATE_TEMPLATE} type object
     * @returns {{required: boolean, between: [number, number|number]}}
     */
    getDayRules (dateObject) {
      const { month, year = new Date().getFullYear() } = dateObject;
      const maxDays = month ? new ExtDate(year, month - 1).getDaysInMonth() : 31;

      return {
        required: true,
        between: [1, maxDays],
      };
    },
    change () {
      const dateIsFulfilled = this.isDayShown
        ? checkDateObjFilled(this.date)
        : checkDateObjFilled(this.date, ['month', 'year']);

      if (dateIsFulfilled) {
        this.parsedDate = null;
      } else {
        const date = this.isDayShown
          ? this.convertDateObjToDate(this.date)
          : this.convertDateObjWithoutDayToDate(this.date);

        this.parsedDate = date.isValid() ? date.format() : null;
      }

      this.$emit('date-change', this.parsedDate);
    },
    getDateErrors (providerFailedRules, errors) {
      if (this.isDayShown) {
        if (this.isDayFieldInvalid) return this.formObserver?.errors[this.vid + '_day'][0];
      }

      if (!this.validationErrors && (errors || this.providerShadowState?.errors?.length)) {
        return errors[0] || this.providerShadowState?.errors[0];
      }
      const fieldErrors = this.validationErrors || errors;
      const fieldName = Object.keys(this.validationErrors)[0];
      const failedRules = providerFailedRules || this.providerShadowState?.failedRules;

      return this.helpers.retrieveFieldErrors(fieldName, failedRules, fieldErrors, this)[0];
    },
    setParsedDateProvider () {
      this.parsedDateProvider = this.$refs.parsedDateProvider;
    },
    async setProviderShadowState () {
      this.providerShadowState = await this.$refs.parsedDateProvider.validateSilent();
    },
  },
  setup () {
    const {
      convertDateObjToDate,
      getDateObj,
    } = useDateSelect();

    return {
      ExtDate,

      convertDateObjToDate,
      getDateObj,
    };
  },
};
</script>
